import React, { useEffect, useMemo, useRef } from 'react';
import { IconClose } from './icons';

const NODE_W = 190;
const NODE_H = 48;
const COL_GAP = 110;
const ROW_GAP = 22;
const PAD = 24;

/**
 * Column per cube: cubes nothing joins to start on the left; a cube sits one
 * column right of the furthest cube that joins to it (cycle-safe).
 */
export function layoutRelationships(cubes) {
  const linked = cubes.filter((c) => c.joins.length > 0 || cubes.some((o) => o.joins.some((j) => j.to === c.name)));
  const names = new Set(linked.map((c) => c.name));
  const column = new Map(linked.map((c) => [c.name, 0]));
  for (let pass = 0; pass < linked.length; pass += 1) {
    let changed = false;
    for (const c of linked) {
      for (const j of c.joins) {
        if (!names.has(j.to)) continue;
        const next = column.get(c.name) + 1;
        if (next > column.get(j.to) && next < linked.length) { column.set(j.to, next); changed = true; }
      }
    }
    if (!changed) break;
  }
  const columns = [];
  for (const c of linked) {
    const ci = column.get(c.name);
    if (!columns[ci]) columns[ci] = [];
    columns[ci].push(c);
  }
  // Order each column by the average row of the cubes that join to it, to cut crossings.
  const rowOf = new Map();
  columns.forEach((col, ci) => {
    if (ci > 0) {
      const weight = (c) => {
        const sources = linked.filter((s) => s.joins.some((j) => j.to === c.name) && rowOf.has(s.name)).map((s) => rowOf.get(s.name));
        return sources.length ? sources.reduce((a, b) => a + b, 0) / sources.length : Infinity;
      };
      col.sort((a, b) => weight(a) - weight(b));
    }
    col.forEach((c, ri) => rowOf.set(c.name, ri));
  });
  const rows = Math.max(1, ...columns.map((c) => c?.length || 0));
  const positions = new Map();
  columns.forEach((col, ci) => {
    const offset = ((rows - col.length) * (NODE_H + ROW_GAP)) / 2;
    col.forEach((c, ri) => positions.set(c.name, { x: PAD + ci * (NODE_W + COL_GAP), y: PAD + offset + ri * (NODE_H + ROW_GAP) }));
  });
  const isolated = cubes.filter((c) => !names.has(c.name));
  return {
    positions,
    edges: linked.flatMap((c) => c.joins.filter((j) => names.has(j.to)).map((j) => ({ from: c.name, to: j.to, on: j.on }))),
    width: PAD * 2 + Math.max(1, columns.length) * NODE_W + Math.max(0, columns.length - 1) * COL_GAP,
    height: PAD * 2 + rows * NODE_H + (rows - 1) * ROW_GAP,
    isolated,
  };
}

/** Dialog showing how datasets relate, with the ones this report uses highlighted. */
const RelationshipMap = ({ catalogue, inUse, joinsUsed, onClose }) => {
  const datasets = useMemo(() => catalogue.cubes.filter((c) => !c.isMetric), [catalogue]);
  const layout = useMemo(() => layoutRelationships(datasets), [datasets]);
  const used = new Set(joinsUsed.map((e) => `${e.from}>${e.to}`));
  const closeRef = useRef(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const edgePath = (e) => {
    const a = layout.positions.get(e.from);
    const b = layout.positions.get(e.to);
    const forward = b.x > a.x;
    const x1 = forward ? a.x + NODE_W : a.x + NODE_W / 2;
    const y1 = forward ? a.y + NODE_H / 2 : a.y + NODE_H;
    const x2 = forward ? b.x : b.x + NODE_W / 2;
    const y2 = forward ? b.y + NODE_H / 2 : b.y;
    const mx = (x1 + x2) / 2;
    const d = forward ? `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}` : `M${x1},${y1} C${x1},${y1 + 30} ${x2},${y2 - 30} ${x2},${y2}`;
    return { d, lx: forward ? x1 + 8 : x1 + 6, ly: forward ? y1 - 5 : y1 + 14 };
  };

  return (
    <div className="rf-dialog-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rf-dialog rf-dialog--wide" role="dialog" aria-modal="true" aria-label="Relationship map">
        <div className="rf-dialog-head">
          <div>
            <h3>Relationship map</h3>
            <p>Arrows point from a dataset to the dataset it looks up. Fields can be combined in one report when the dataset holding the values can reach the others by following arrows.</p>
          </div>
          <button ref={closeRef} type="button" className="rf-icon-btn" onClick={onClose} aria-label="Close"><IconClose size={16} /></button>
        </div>
        <div className="rf-map-scroll">
          {layout.edges.length === 0 ? (
            <div className="rf-empty">No relationships are declared between this company’s datasets. Relationships are defined by the dataset templates or on the Data sources page.</div>
          ) : (
            <svg className="rf-map" viewBox={`0 0 ${layout.width} ${layout.height}`} width={layout.width} height={layout.height}>
              <defs>
                <marker id="rf-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerUnits="userSpaceOnUse" markerWidth="9" markerHeight="9" orient="auto-start-reverse">
                  <path d="M0,0 L10,5 L0,10 z" className="rf-map-arrow" />
                </marker>
                <marker id="rf-arrow-on" viewBox="0 0 10 10" refX="9" refY="5" markerUnits="userSpaceOnUse" markerWidth="9" markerHeight="9" orient="auto-start-reverse">
                  <path d="M0,0 L10,5 L0,10 z" className="rf-map-arrow rf-map-arrow--on" />
                </marker>
              </defs>
              {layout.edges.map((e) => {
                const on = used.has(`${e.from}>${e.to}`);
                const { d, lx, ly } = edgePath(e);
                const key = e.on.split('=')[0].trim();
                return (
                  <g key={`${e.from}>${e.to}`} className={`rf-map-edge ${on ? 'rf-map-edge--on' : ''}`}>
                    <title>{`${e.from}.${e.on}`}</title>
                    <path d={d} markerEnd={`url(#${on ? 'rf-arrow-on' : 'rf-arrow'})`} />
                    <text x={lx} y={ly}>{key}</text>
                  </g>
                );
              })}
              {datasets.filter((c) => layout.positions.has(c.name)).map((c) => {
                const p = layout.positions.get(c.name);
                const on = inUse.has(c.name);
                return (
                  <g key={c.name} className={`rf-map-node ${on ? 'rf-map-node--on' : ''}`} transform={`translate(${p.x},${p.y})`}>
                    <rect width={NODE_W} height={NODE_H} rx="8" />
                    <text x="12" y="20" className="rf-map-node-title">{c.title.length > 24 ? `${c.title.slice(0, 23)}…` : c.title}</text>
                    <text x="12" y="37" className="rf-map-node-sub">{c.measures.length} measures · {c.dimensions.length} dimensions</text>
                  </g>
                );
              })}
            </svg>
          )}
          {layout.isolated.length > 0 && (
            <div className="rf-map-isolated">
              <span>No relationships:</span>
              {layout.isolated.map((c) => <span key={c.name} className={`rf-map-pill ${inUse.has(c.name) ? 'rf-map-pill--on' : ''}`}>{c.title}</span>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RelationshipMap;
